

import { mapGetters } from 'vuex';
import { removeWhitespaces } from '@sopheos/orbjs_rules';

// API
import * as apiContributions from '../api/private/contributions';

import errorsMixin from '../mixins/error-handler';

// Async components
import SLoadingState from './SLoadingState.vue';
import SErrorState from './SErrorState.vue';

const SUploadImageLazy = () => ({
  component: import('./SUploadImage.vue'),
  loading: SLoadingState,
  error: SErrorState,
  timeout: 60000,
});

export default {
  name: 'SDetailScheduleForm',
  components: {
    SUploadImageLazy,
  },
  mixins: [errorsMixin],
  props: {
    hasContribution: {
      type: Boolean,
      default: false,
    },
    loadingContributions: {
      type: Boolean,
      default: false,
    },
    currentPlayground: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      fieldsRefs: ['contribution_schedule-description', 'contribution_schedule-form', 'contribution_schedule-playground', 'contribution_schedule-uuid'],
      editSchedule: false,
      loadingEditSchedule: false,
      schedulePanel: [0],
      scheduleDesc: null,
      scheduleUuid: null,
      noSchedule: false,
      contributionValue: null,
      dialogScheduleTitle: 'Horaires',
      dialogScheduleLabel: 'Contribution - Horaires',
      dialogScheduleButtons: {
        closeText: 'Fermer',
        confirmText: 'Enregistrer',
      },
      dialogScheduleOptions: {
        maxWidth: '500px',
        scrollable: true,
      },
      dialogScheduleLoading: false,
      toggleTooltip: false,
      waitEditSchedule: false,
      showDeleteContribution: false,
    };
  },
  computed: {
    ...mapGetters({
      isAuth: 'authToken/isAuth',
      userSecured: 'preferences/userSecured',
    }),
    dialogSchedule: {
      get() {
        return this.editSchedule && this.userSecured;
      },
      set(value) {
        this.editSchedule = value;
      },
    },
    scheduleDescIsSet() {
      // The description is not null and is not empty
      return this.scheduleDesc?.length > 0;
    },
    scheduleDescIsValid() {
      // The description is set and contains between 10 and 1000 characters
      return this.scheduleDescIsSet && this.scheduleDesc.length >= 10 && this.scheduleDesc.length <= 1000;
    },
    scheduleEmpty() {
      const empty = [
        'Aucun horaire renseigné pour le moment.',
        'Les horaires n\'ont pas été renseignés.',
        'Il manque des informations sur les horaires de cette aire de jeux.',
        'Les horaires ne nous ont pas encore été communiqués pour cette aire de jeux.',
        'Les heures d\'ouverture sont manquantes.',
        'Pour le moment, nous n\'avons pas connaissance des heures d\'ouverture.',
      ];

      const cta = [
        'Soyez le premier à nous les indiquer !',
        'Aidez-nous à compléter ces informations en renseignant les horaires.',
        'Si vous connaissez les horaires de cette aire de jeux, n\'hésitez pas à nous les communiquer.',
        'Gagnez des niveaux de contributeur en complétant les horaires !',
        'Contribuez en nous indiquant les horaires de cette aire de jeux.',
        'Votre aide est la bienvenue pour renseigner cette information manquante.',
        'Merci de nous aider à compléter cette fiche en remplissant les horaires.',
        'Vous connaissez l\'aire de jeux ? Partagez ce que vous savez avec la communauté !',
      ];

      return `${empty[Math.floor(Math.random() * empty.length)]} ${cta[Math.floor(Math.random() * cta.length)]}`;
    },
  },
  watch: {
    isAuth(value) {
      if (!value) {
        this.editSchedule = false;
      }
    },
    hasContribution() {
      this.contributionValue = this.hasContribution;
    },
    async loadingContributions() {
      if (!this.loadingContributions && this.userSecured && this.waitEditSchedule) {
        await this.getScheduleContributions();
        this.editSchedule = true;
        this.waitEditSchedule = false;
      }
    },
    scheduleDesc() {
      // When the description changes, if it is not set or if it is valid, set the error to false
      if (!this.scheduleDescIsSet || this.scheduleDescIsValid) {
        this.errors$.handleError(false, 'contribution_schedule', 'description');
      }
    },
    dialogSchedule() {
      this.$emit('has-unsave', this.dialogSchedule);
    },
  },
  mounted() {
    this.contributionValue = this.hasContribution;
  },
  methods: {
    showLogin() {
      if (!this.userSecured) {
        this.$emit('login');
        this.waitEditSchedule = true;
      }
    },
    async showScheduleForm() {
      this.showLogin();
      if (!this.waitEditSchedule) {
        if (this.contributionValue) {
          await this.getScheduleContributions();
        }
        this.editSchedule = true;
        this.errors$.reset();
      }
    },
    closeScheduleForm() {
      this.editSchedule = false;
      this.schedulePanel = [0];
      this.scheduleDesc = null;
      this.scheduleUuid = null;
      this.waitEditSchedule = false;
    },
    async getScheduleContributions() {
      this.loadingEditSchedule = true;
      await apiContributions.contributionsById(this.currentPlayground.id, { type: 'schedule' }, this).then((contributions) => {
        this.scheduleDesc = contributions.schedule.description;
        if (this.scheduleDesc) {
          this.schedulePanel.push(1);
        }
        this.scheduleUuid = contributions.schedule.photo;
      // Empty catch, because we don't want to display an error message. Non-blocking feature.
      }).catch(() => null).finally(() => {
        this.loadingEditSchedule = false;
      });
    },
    submit() {
      if (this.noSchedule) {
        this.scheduleUuid = null;
        this.scheduleDesc = 'Aucun horaire renseigné';
      }
      this.scheduleDesc = removeWhitespaces(this.scheduleDesc);
      // prevalidation
      this.errors$.reset();
      this.errors$.handleError(this.scheduleDescIsSet && !this.scheduleDescIsValid, 'contribution_schedule', 'description');
      const submitValidation = !this.scheduleDescIsSet && !this.scheduleUuid;
      this.errors$.handleError(submitValidation, 'contribution_schedule', 'form', 'description_or_image_required');
      if (this.errors$.hasErrors()) {
        if (submitValidation) {
          this.schedulePanel.push(0);
        }
        if (!this.scheduleDescIsValid) {
          this.schedulePanel.push(1);
        }
        this.errors$.focusFirstError();
        return;
      }
      const data = {
        playground: this.currentPlayground.id,
        description: this.scheduleDesc,
        uuid: this.scheduleUuid,
      };

      this.dialogScheduleLoading = true;
      apiContributions.scheduleContribution(data, this)
        .then(() => {
          this.closeScheduleForm();
          this.contributionValue = true;
          this.$emit('contribution', {
            description: data.description,
            photo: data.uuid,
          });
        }).catch((error) => {
          // Validation error
          if (error.response && error.response.status === 400) {
            if (this.errors$.hasErrors()) {
              this.errors$.focusFirstError();
              return;
            }
            this.errors$.handleError(true, 'contribution_schedule', 'form');
            this.$nextTick(() => {
              this.$refs['contribution_schedule-form'].$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
          }
        }).finally(() => {
          this.dialogScheduleLoading = false;
        });
    },
    deleteContribution() {
      this.closeScheduleForm();
      this.contributionValue = false;
      this.$emit('delete-contribution');
    },
  },
};
